import axios from "axios";
// No mock data import

const API_URL = process.env.REACT_APP_API_URL || "https://adani-cimapv3.onrender.com/api";
const LOCAL_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 12000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("osint_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("osint_token");
      localStorage.removeItem("osint_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

/**
 * safeFetch: try the external API. Returns real data or empty results.
 * No mock fallbacks to ensure data integrity.
 */
export async function safeFetch(path, { params = {}, signal = null } = {}) {
  try {
    const res = await apiClient.get(path, { params, signal });
    return { data: res.data, source: "live" };
  } catch (err) {
    console.error(`API Error (${path}):`, err.message);
    // Return empty structures to prevent UI crashes while maintaining "no fake data"
    const emptyData = path.includes("dist") || path.includes("trend") || path.includes("posts") ? [] : null;
    return { data: emptyData, source: "offline", error: err?.message };
  }
}

export async function loginExternal(email, password) {
  try {
    const res = await apiClient.post("/auth/login", { email, password });
    return { ok: true, data: res.data, source: "live" };
  } catch (err) {
    return { ok: false, error: err?.response?.data?.detail || err.message };
  }
}

export async function fetchMe() {
  try {
    const res = await apiClient.get("/auth/me");
    return { ok: true, data: res.data };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

export async function exportPosts(params = {}) {
  const token = localStorage.getItem("osint_token");
  const qs = new URLSearchParams({ ...params, access_token: token || "" }).toString();
  window.open(`${API_URL}/export/csv?${qs}`, "_blank");
}

export async function exportPDF(params = {}) {
  const token = localStorage.getItem("osint_token");
  const qs = new URLSearchParams({ ...params, access_token: token || "" }).toString();
  window.open(`${API_URL}/export/pdf?${qs}`, "_blank");
}

export async function aiChat(message, sessionId) {
  const token = localStorage.getItem("osint_token");
  // Try external directly first (if real backend)
  try {
    const res = await apiClient.post(
      "/chat/analyst",
      { message, session_id: sessionId },
      { timeout: 18000 }
    );
    const data = res.data;
    const text = typeof data === "string" ? data : data.response || data.reply || JSON.stringify(data);
    return { response: text, source: "external" };
  } catch (e) {
    // fallback to local proxy which has emergent LLM
    try {
      const res = await axios.post(
        `${LOCAL_URL}/ai/chat`,
        { message, session_id: sessionId, token: token || null },
        { timeout: 25000 }
      );
      return { response: res.data.response, source: res.data.source };
    } catch (e2) {
      return {
        response:
          "AI Analyst currently unreachable. Please retry shortly.\n\nTip: when live, I correlate signals, narrative clusters and geographic risk.",
        source: "offline",
      };
    }
  }
}

export { API_URL, LOCAL_URL };
